import { useState, useMemo } from 'react';
import { Table, Button, Form, Tabs, Tab, Badge } from 'react-bootstrap';
import { FaTimes, FaSort, FaSortUp, FaSortDown, FaBoxOpen } from 'react-icons/fa';
import Loader from '../components/Loader';
import Message from '../components/Message';
import { Link } from 'react-router-dom';
import { useGetMyOrdersQuery } from '../slices/ordersApiSlice';
import SummaryHeader from '../components/SummaryHeader';

const MyOrdersScreen = () => {
    const { data: orders = [], isLoading, error } = useGetMyOrdersQuery();

    const [searchQuery, setSearchQuery] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(25);
    const [activeTab, setActiveTab] = useState('orders');

    // USER ORDERS (stable reference)
    const userOrders = useMemo(() => orders, [orders]);

    // SUMMARY (like admin)
    const summary = useMemo(() => {
        let totalPaid = 0,
            totalUnpaid = 0,
            totalBooks = 0,
            deliveredCount = 0,
            deletedItemsPrice = 0;

        userOrders.forEach(o => {
        const itemsPrice = Number(o.itemsPrice ?? 0);

        if (o.isPaid) totalPaid += itemsPrice;
        else totalUnpaid += itemsPrice;

        totalBooks += o.orderItems?.length ?? 0;
        if (o.isDelivered) deliveredCount += 1;

        if (!o.isDelivered && Array.isArray(o.removedItems)) {
            o.removedItems.forEach(item => {
            deletedItemsPrice += Number(item.accepted_price ?? item.offer_price ?? item.price ?? 0);
            });
        }
        });

        return { totalPaid, totalUnpaid, totalBooks, deliveredCount, deletedItemsPrice };
    }, [userOrders]);

    // FILTER + SORT
    const filteredSortedOrders = useMemo(() => {
        let list = [...userOrders];

        if (searchQuery) {
        const q = searchQuery.toLowerCase();
        list = list.filter(o => {
            const dateMatch = o.createdAt?.substring(0,10).includes(q);
            const activeMatch = o.orderItems?.some(item => item.isbn?.toString().toLowerCase().includes(q));
            const deletedMatch = o.removedItems?.some(item => item.isbn?.toString().toLowerCase().includes(q));
            return dateMatch || activeMatch || deletedMatch;
        });
        }

        list.sort((a,b) => {
        const { key, direction } = sortConfig;
        let valA, valB;
        switch(key){
            case 'date': valA = new Date(a.createdAt); valB = new Date(b.createdAt); break;
            case 'total': valA = Number(a.itemsPrice); valB = Number(b.itemsPrice); break;
            case 'books': valA = a.orderItems?.length ?? 0; valB = b.orderItems?.length ?? 0; break;
            case 'paid': valA = a.isPaid; valB = b.isPaid; break;
            case 'delivered': valA = a.isDelivered; valB = b.isDelivered; break;
            default: valA = 0; valB = 0;
        }
        if(valA < valB) return direction==='asc' ? -1 : 1;
        if(valA > valB) return direction==='asc' ? 1 : -1;
        return 0;
        });

        return list;
    }, [userOrders, searchQuery, sortConfig]);

    const pageCount = Math.ceil(filteredSortedOrders.length / itemsPerPage);
    const paginatedOrders = filteredSortedOrders.slice((currentPage-1)*itemsPerPage, currentPage*itemsPerPage);

    const allRemovedItems = useMemo(() => {
        const list = [];
        userOrders.forEach(o => {
        o.removedItems?.forEach(item => {
            list.push({ ...item, orderId: o._id, date: o.createdAt?.substring(0,10) });
        });
        });
        return list;
    }, [userOrders]);

    const handleSort = key => {
        setSortConfig(prev => prev.key === key ? { key, direction: prev.direction==='asc'?'desc':'asc' } : { key, direction:'asc' });
    };
    const renderSortIcon = key => sortConfig.key !== key ? <FaSort /> : sortConfig.direction==='asc'?<FaSortUp />:<FaSortDown />;

    return (
        <>
            <h4 className="d-flex justify-content-center align-items-center gap-2 mb-0">
                <FaBoxOpen /> Mijn Bestellingen
                <Badge bg="secondary">{orders.length}</Badge>
            </h4>

            <SummaryHeader
                totalPaid={summary.totalPaid}
                totalUnpaid={summary.totalUnpaid}
                totalBooks={summary.totalBooks}
                totalOrders={userOrders.length}
                deliveredCount={summary.deliveredCount}
                deletedItemsPrice={summary.deletedItemsPrice}
            />

            {/* SEARCH BAR */}
            <div className="my-3 mx-auto" style={{ maxWidth: '600px' }}>
                <Form.Control
                type="text"
                placeholder="Zoek op Order Datum of ISBN..."
                value={searchQuery}
                onChange={e => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                />
            </div>

            {isLoading ? <Loader /> :
                error ? <Message variant="danger">{error?.data?.message || error.error}</Message> :
                userOrders.length===0 ? <Message variant="info">Geen bestellingen gevonden</Message> :
                <>
                    <Tabs activeKey={activeTab} onSelect={k=>setActiveTab(k)} className="mb-3">
                        <Tab eventKey="orders" title={`Bestellingen (${userOrders.length})`}>
                            <Table striped bordered hover responsive>
                                <thead>
                                <tr>
                                    <th onClick={()=>handleSort('date')} style={{cursor:'pointer'}}>Datum {renderSortIcon('date')}</th>
                                    <th onClick={()=>handleSort('total')} style={{cursor:'pointer'}}>Totaal {renderSortIcon('total')}</th>
                                    <th>Totaal Verwijderd</th>
                                    <th onClick={()=>handleSort('books')} style={{cursor:'pointer'}}>Boeken {renderSortIcon('books')}</th>
                                    <th>Verwijderd</th>
                                    <th onClick={()=>handleSort('paid')} style={{cursor:'pointer'}}>Betaald {renderSortIcon('paid')}</th>
                                    <th onClick={()=>handleSort('delivered')} style={{cursor:'pointer'}}>Geleverd {renderSortIcon('delivered')}</th>
                                    <th>Detail</th>
                                </tr>
                                </thead>
                                <tbody>
                                {paginatedOrders.map(order => {
                                    const deletedItemsCount = order.removedItems?.length ?? 0;
                                    const deletedItemsPrice = order.removedItems?.reduce((sum,item)=>sum + Number(item.accepted_price??item.offer_price??item.price??0),0);
                                    return (
                                    <tr key={order._id} className={!order.isDelivered?'table-warning':''}>
                                        <td>{order.createdAt?.substring(0,10)}</td>
                                        <td>€ {Number(order.itemsPrice).toFixed(2)}</td>
                                        <td>€ {deletedItemsPrice.toFixed(2)}</td>
                                        <td>{order.orderItems?.length ?? 0}</td>
                                        <td>{deletedItemsCount>0?deletedItemsCount:'—'}</td>
                                        <td>{order.isPaid ? <Badge bg="info">{order.paidAt?.substring(0,10)}</Badge> : <FaTimes color="red"/>}</td>
                                        <td>{order.isDelivered ? <Badge bg="success">{order.deliveredAt?.substring(0,10)}</Badge> : <FaTimes color="red"/>}</td>
                                        <td><Button as={Link} to={`/order/${order._id}`} size="sm">Bekijk</Button></td>
                                    </tr>
                                    );
                                })}
                                </tbody>
                            </Table>
                        </Tab>
                        <Tab eventKey="deleted" title={`Verwijderd (${allRemovedItems.length})`}>
                        {allRemovedItems.length===0 ? <Message variant="info">Geen verwijderde items gevonden</Message> :
                            <Table striped bordered hover responsive>
                                <thead>
                                <tr>
                                    <th>Datum</th>
                                    <th>ISBN</th>
                                    <th>Titel</th>
                                    <th>Prijs</th>
                                    <th>Order</th>
                                </tr>
                                </thead>
                                <tbody>
                                {allRemovedItems.map(it => (
                                    <tr key={it._id}>
                                    <td>{it.date}</td>
                                    <td>{it.isbn}</td>
                                    <td>{it.title}</td>
                                    <td>€ {Number(it.accepted_price??it.offer_price??it.price??0).toFixed(2)}</td>
                                    <td><Button as={Link} to={`/order/${it.orderId}`} size="sm">Bekijk</Button></td>
                                    </tr>
                                ))}
                                </tbody>
                            </Table>}
                        </Tab>
                    </Tabs>

                    {/* PAGINATION */}
                    <div className="d-flex justify-content-between align-items-center">
                        <Button disabled={currentPage===1} onClick={()=>setCurrentPage(p=>p-1)}>Vorige</Button>
                        <span>Pagina {currentPage} van {pageCount}</span>
                        <Button disabled={currentPage===pageCount} onClick={()=>setCurrentPage(p=>p+1)}>Volgende</Button>
                        <div className="d-flex align-items-center gap-2">
                        <span>Per pagina:</span>
                        <Form.Select size="sm" value={itemsPerPage} onChange={e=>{setItemsPerPage(Number(e.target.value)); setCurrentPage(1);}} style={{width:'90px'}}>
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </Form.Select>
                        </div>
                        <small className="text-muted">
                            Toon {(currentPage - 1) * itemsPerPage + 1}–
                            {Math.min(currentPage * itemsPerPage, filteredSortedOrders.length)}
                            {' '}van {filteredSortedOrders.length} bestellingen
                        </small>
                    </div>
                </>
            }
        </>
    );
};

export default MyOrdersScreen;